import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "../../components/PageHeader";
import ButtonFilled from "../../components/FormElements/ButtonFilled";
import { fetchCampaigns } from "../../store/campaignStore/campaignActions";
import styles from "./campaigns.module.scss";
import useUsdKzt, { fmtKzt } from "../../hooks/useUsdKzt";

const PERIOD_OPTIONS = [7, 30, 90, 0];
const campaignTs = (c) => Number(c.completed_at || c.started_at || c.created_at || 0);
const fmtUsd = (n, currency = "USD") => `${(Number(n) || 0).toFixed(2)} ${currency}`;
const fmtMoney = (n, currency, rate) =>
    (currency === "USD" && fmtKzt(n, rate)) || fmtUsd(n, currency);

const STATUS_LABEL_KEY = {
    draft: "NSCampaigns.statusDraft",
    scheduled: "NSCampaigns.statusScheduled",
    in_progress: "NSCampaigns.statusInProgress",
    paused: "NSCampaigns.statusPaused",
    completed: "NSCampaigns.statusCompleted",
    failed: "NSCampaigns.statusFailed",
    cancelled: "NSCampaigns.statusCancelled",
};

const formatSentDate = (c) => {
    const ts = c.completed_at || c.started_at || c.created_at;
    if (!ts) return "—";
    return new Date(Number(ts)).toLocaleDateString();
};

const CampaignsList = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { t } = useTranslation();
    const usdKzt = useUsdKzt();
    const branchID = useSelector((s) => s.branchReducer.info?.id);
    const list = useSelector((s) => s.campaignReducer.list);
    const stats = useSelector((s) => s.campaignReducer.stats);
    const status = useSelector((s) => s.campaignReducer.listStatus);

    const [period, setPeriod] = useState(30);

    useEffect(() => {
        if (branchID) {
            dispatch(fetchCampaigns(branchID, {}));
        }
    }, [branchID, dispatch]);

    const filteredList = useMemo(() => {
        if (!period) return list;
        const since = Date.now() - period * 24 * 60 * 60 * 1000;
        return list.filter((c) => campaignTs(c) >= since);
    }, [list, period]);

    const spend = useMemo(() => {
        let messages = 0;
        let cost = 0;
        let currency = "USD";
        filteredList.forEach((c) => {
            const id = c._id || c.id;
            messages += (stats && stats[id]?.sent) || 0;
            cost += Number(c.estimated_cost) || 0;
            if (c.estimated_currency) currency = c.estimated_currency;
        });
        return { messages, cost, currency, avg: messages ? cost / messages : 0 };
    }, [filteredList, stats]);

    const hasActive = list.some(
        (c) => c.status === "in_progress" || c.status === "paused"
    );

    const showEmpty = list.length === 0 && status !== "PENDING";

    return (
        <div className={styles.container}>
            <div className={styles.listHeader}>
                <PageHeader title={t("NSCampaigns.title")} />
                <Link to="/campaigns/new">
                    <ButtonFilled
                        title={hasActive ? t("NSCampaigns.anotherCampaignActiveTooltip") : undefined}
                    >
                        {t("NSCampaigns.createButton")}
                    </ButtonFilled>
                </Link>
            </div>

            {status === "PENDING" && <p className={styles.loading}>...</p>}

            {!showEmpty && (
                <div className={styles.estimateBlock}>
                    <div className={styles.listHeader}>
                        <label className={styles.statLabel}>{t("NSCampaigns.spendTitle")}</label>
                        <div className={styles.filterBar}>
                            {PERIOD_OPTIONS.map((p) => (
                                <button
                                    key={p}
                                    type="button"
                                    className={period === p ? styles.filterActive : styles.filter}
                                    onClick={() => setPeriod(p)}
                                >
                                    {p ? t("NSCampaigns.windowDays", { days: p }) : t("NSCampaigns.periodAll")}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className={styles.statCards}>
                        <div className={styles.statCard}>
                            <span className={styles.statValue}>{spend.messages}</span>
                            <span className={styles.statLabel}>{t("NSCampaigns.kpiMessagesSent")}</span>
                        </div>
                        <div className={styles.statCard}>
                            <span className={styles.statValue}>{fmtMoney(spend.cost, spend.currency, usdKzt)}</span>
                            <span className={styles.statLabel}>{t("NSCampaigns.kpiSpent")}</span>
                        </div>
                        <div className={styles.statCard}>
                            <span className={styles.statValue}>{fmtMoney(spend.avg, spend.currency, usdKzt)}</span>
                            <span className={styles.statLabel}>{t("NSCampaigns.kpiAvgPerMsg")}</span>
                        </div>
                    </div>
                </div>
            )}

            {showEmpty ? (
                <div className={styles.empty}>
                    <p>{t("NSCampaigns.emptyState")}</p>
                    <Link to="/campaigns/new">
                        <ButtonFilled>{t("NSCampaigns.createButton")}</ButtonFilled>
                    </Link>
                </div>
            ) : (
                <div className={styles.tableWrap}>
                    <table className={styles.campaignTable}>
                        <thead>
                            <tr>
                                <th>{t("NSCampaigns.colName")}</th>
                                <th>{t("NSCampaigns.colTemplate")}</th>
                                <th>{t("NSCampaigns.colStatus")}</th>
                                <th>{t("NSCampaigns.colSentDate")}</th>
                                <th>{t("NSCampaigns.colTotal")}</th>
                                <th>{t("NSCampaigns.colSent")}</th>
                                <th>{t("NSCampaigns.colDelivered")}</th>
                                <th>{t("NSCampaigns.colRead")}</th>
                                <th>{t("NSCampaigns.colFailed")}</th>
                                <th>{t("NSCampaigns.colCost")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredList.map((c) => {
                                const id = c._id || c.id;
                                const s = (stats && stats[id]) || {};
                                return (
                                    <tr
                                        key={id}
                                        className={styles.clickableRow}
                                        onClick={() => history.push(`/campaigns/${id}`)}
                                    >
                                        <td>{c.audience_label || c.name}</td>
                                        <td>{c.message?.template_name || "—"}</td>
                                        <td>
                                            <span className={`${styles.status} ${styles[`status_${c.status}`]}`}>
                                                {t(STATUS_LABEL_KEY[c.status] || c.status)}
                                            </span>
                                        </td>
                                        <td>{formatSentDate(c)}</td>
                                        <td>{s.total || 0}</td>
                                        <td>{s.sent || 0}</td>
                                        <td>{s.delivered || 0}</td>
                                        <td>{s.read || 0}</td>
                                        <td>{s.failed || 0}</td>
                                        <td>{fmtMoney(c.estimated_cost, c.estimated_currency || "USD", usdKzt)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default CampaignsList;
